import React, { useCallback, useMemo, useState } from 'react';
import {
  Activity,
  ActivityTitle,
  ItemBlock,
  ItemChange,
  SubTitle,
  ActivityContentView,
  ActivitySubContent,
  Text,
  ActivityView,
  BorderDiv,
  RightSection,
  Portion,
  ReactionDiv,
  EditIcon,
  Div,
  Div2,
  StatusIcon,
  SubText,
  SvgFile,
  FileIconDiv,
  FileType,
  ReactionContainer,
  ReactionShow,
  ReactionShowWrapper,
  ActivitySubContentLeft,
  CompanyName,
  CompanySection,
  FileSection
} from '../styles';
import {
  AVATAR_BORDER,
  COMMENT_TYPE,
  DEFAULT_DATE_FORMAT,
  FILE_EXTENSIONS,
  FILE_TYPES,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  MENU_OPTIONS,
  STATUS_TYPE,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS,
  USER_ROLE,
  dropdownMenuItems,
  responsiveDropdownMenuItems
} from '../../../../../global/constants';
import Avatar from '../../../../../component/avatar';
import { categorizeFiles, extensionName, floatToHHMM, isEmpty } from '../../../../../helpers/common';
import Emoji from '../../../../../component/emojiPickerDropdown';
import CommentInputBox from '../../../../../component/inputBox/commentInputBox';
import { Dropdown, DropdownItem } from '../../../../../component/dropdown';
import Tooltip from '../../../../../component/tooltip';
import IconButton from '../../../../../component/button/iconButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EmojiClickData } from 'emoji-picker-react';
import { UserInterface } from '../../../../../interfaces/ProjectInterface';
import { TaskActivityProps, CommentFilesInterface } from '../../../../../interfaces/TaskInterface';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../../../../mobx/rootStore';
import { useMobileDevice } from '../../../../../global/useMobile';
import UserPreferenceSingleton from '../../../../../helpers/userPreferenceSingleton';
import { CommentModel } from '../../../../../mobx/models/comment';
import { UserModel } from '../../../../../mobx/models/user';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import RenderHtml from '../../../../../component/renderHtml';
import { RenderImagePreview } from '../../../../../component/renderImagePreview';
import RenderVideoFile from '../../../../../component/renderVideoFile';
import { RenderFilePreview } from '../../../../../component/renderFilePreview';
import FileSVGIcon from '../../../../../assets/images/svg/filesIconSvg';
import { addReactionOnCommect, getMyTaskDetailsData } from '../../../../../services/taskServices';
import { trackAnalyticActivity } from '../../../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../../../global/analyticsConstants';
import { RawReaction } from '../../../../../mobx/interfaces/comment';
import Modal from '../../../../../component/modals/modal';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../../../routes/urls';
dayjs.extend(relativeTime);

interface CommentReactionsProps {
  item: CommentModel;
  emojiLoading: boolean;
  workspaceUsers: UserModel[] | undefined;
  onReactOnMessage: (emojiData: { emoji: string }, item: { id: string }) => void;
}

const CommentReactions = observer(function CommentReactions({
  item,
  emojiLoading,
  workspaceUsers,
  onReactOnMessage
}: CommentReactionsProps) {
  const [hoveredReaction, setHoveredReaction] = useState<string>();

  if (!item?.reactions?.length) return null;

  const reactionCounts = item.reactions.reduce((acc: { [key: string]: number }, { ReactionType }: RawReaction) => {
    acc[ReactionType] = (acc[ReactionType] || 0) + 1;
    return acc;
  }, {});

  return (
    <ReactionContainer>
      <Dropdown
        content={
          <ReactionShowWrapper>
            {Object.entries(reactionCounts).map(([type, count]) => (
              <ReactionShow
                role='button'
                key={`reactions_${type}_${count}`}
                onClick={() => onReactOnMessage({ emoji: type }, item)}
                onMouseOver={() => setHoveredReaction(type)}
                isDisabled={emojiLoading}>
                <div className='reaction-icon'>{type}</div>
                <p>{count}</p>
              </ReactionShow>
            ))}
          </ReactionShowWrapper>
        }
        trigger='hover'>
        {Object.entries(reactionCounts).map(([type]) => (
          <div key={type}>
            {type === hoveredReaction &&
              item?.reactions
                ?.filter((reaction) => reaction.ReactionType === hoveredReaction)
                ?.map((reactedUser) => {
                  const user = workspaceUsers?.find((u) => u?.id === reactedUser?.UserId);
                  return (
                    <DropdownItem
                      key={`Reactions_type_${reactedUser?.UserId}`}
                      label={user?.name}
                      isAvatar={true}
                      avatarSrc={user?.profile_photo || ''}
                    />
                  );
                })}
          </div>
        ))}
      </Dropdown>
    </ReactionContainer>
  );
});

const TaskActivity = observer(function TaskActivity({
  taskId,
  isKeyboardVisible,
  uploadedFiles,
  loading,
  openEmoji,
  selectedMessage,
  replyMessage,
  isOpenComment,
  inlineScrollRef,
  inlineRef,
  onEditComment,
  onDeleteComment,
  figmaRefreshLoading,
  onClickHandleRefreshFigma,
  onClickHandleRichtextClick,
  handleActivityFileCard,
  attachmentPage,
  onSendComment,
  setOpenEmoji,
  setSelectedMessage,
  setReplyMessage,
  onFileChange,
  setLoading,
  handleOpenSnapModal,
  openModal,
  emojiButtonRef,
  buttonDropdownRef
}: TaskActivityProps) {
  const { taskStore, workspaceStore, projectStore, currentUser: userDetails } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  const { currentProject } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const mobile = useMobileDevice();
  const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
  const currentEmoji = UserPreferenceSingleton.getInstance().getUserUseEmoji();
  const history = useHistory();

  // Local state (moved from parent)
  const [emojiLoading, setEmojiLoading] = useState(false);
  const [isEmojiModalOpen, setIsEmojiModalOpen] = useState(false);

  const isViewerPrivileged = useMemo(() => {
    const viewer = workspace?.users?.find((user) => user?.id === currentUser?.id);
    return Boolean(viewer?.isAdmin || viewer?.isOwner);
  }, [workspace?.users, currentUser?.id]);

  const handleCompanyClick = useCallback(
    (companyId: string | undefined, isPrivileged: boolean) => {
      if (isPrivileged && companyId) {
        history.push(ROUTES.CUSTOMER_DETAILS.replace(':id', companyId));
      }
    },
    [history]
  );

  // --- Helper functions ---

  const renderActivityStatusIcon = useCallback((type: number) => {
    switch (type) {
      case STATUS_TYPE.BACKLOG:
        return (
          <SVGIcon
            name='task-status-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.UNSTARTED:
        return (
          <SVGIcon
            name='task-todo-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.IN_PROGRESS:
        return (
          <SVGIcon
            name='task-inprogress-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.IN_REVIEW:
        return (
          <SVGIcon
            name='task-inreview-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.COMPLETED:
        return (
          <SVGIcon
            name='complete-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
            className='complete-icon'
          />
        );
      case STATUS_TYPE.CANCELLED:
        return (
          <SVGIcon
            name='task-cancelled-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
            fill='var(--text-tertiary)'
          />
        );
      default:
        return <></>;
    }
  }, []);

  const getPriorityIcon = useCallback((priority: number | undefined) => {
    if (typeof priority !== 'number') return;
    const iconMap = {
      [TASK_PRIORITY.NO_PRIORITY]: 'Priority-No-priority-icon',
      [TASK_PRIORITY.URGENT]: 'Priority-Urgent-icon',
      [TASK_PRIORITY.HIGH]: 'Priority-High-icon',
      [TASK_PRIORITY.MEDIUM]: 'Priority-Medium-icon',
      [TASK_PRIORITY.LOW]: 'Priority-low-icon'
    };
    return iconMap[priority] || 'Priority-No-priority-icon';
  }, []);

  const renderFiles = (files: CommentFilesInterface[] | undefined) => {
    if (!files || files.length === 0) return null;

    return (
      <div className='file-div'>
        {files.map((file) => {
          const extension = extensionName(file).toLowerCase();
          const isUploadedFile = !file.uploadFileType || file.uploadFileType === FILE_TYPES.UPLOAD;
          const isGenericFile = isUploadedFile && !FILE_EXTENSIONS.includes(extension);
          let iconName: string;
          if (isGenericFile) {
            iconName = 'blank';
          } else if (isUploadedFile) {
            iconName = extension;
          } else {
            iconName = `icon_${file.uploadFileType}`;
          }
          return (
            <div key={file.href} className='image-div'>
              <SvgFile>
                {isGenericFile ? (
                  <FileIconDiv>
                    <FileSVGIcon name='blank' height='32' width='32' viewBox='0 0 60 60' />
                    <FileType fontSize={extension.length === 4 ? 7 : 5} className='file-text'>
                      {extension.length <= 5 && extension.split('.').pop()?.toUpperCase()}
                    </FileType>
                  </FileIconDiv>
                ) : (
                  <FileSVGIcon name={iconName} height='32' width='32' viewBox='0 0 60 60' />
                )}
              </SvgFile>
              <div className='filetext'>{file.name}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // --- Callbacks ---

  const scrollToInline = useCallback(() => {
    if (inlineScrollRef?.current) {
      const rect = inlineScrollRef.current.getBoundingClientRect();
      const isInView =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);

      if (!isInView) {
        inlineScrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [inlineScrollRef]);

  const closeEmojiDropdown = useCallback(() => {
    setOpenEmoji(false);
    setSelectedMessage('');
  }, [setOpenEmoji, setSelectedMessage]);

  const onReactOnMessage = useCallback(
    async (emojiData: { emoji: string }, item: { id: string }) => {
      setEmojiLoading(true);
      closeEmojiDropdown();
      const updatedEmojis = [
        emojiData?.emoji,
        ...(Array.isArray(currentEmoji) ? currentEmoji.filter((e) => e !== emojiData?.emoji) : [])
      ]?.slice(0, 2);
      UserPreferenceSingleton.getInstance().setUserUseEmoji(updatedEmojis);
      const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
      const payload = {
        reaction: emojiData?.emoji,
        commentId: item?.id,
        taskId: taskId,
        userId: userDetails?.id,
        companyId: workspace?.id
      };
      const response = await addReactionOnCommect(payload);
      if (response) {
        trackAnalyticActivity(TASK_ANALYTICS.COMMENT_REACTED);
        getMyTaskDetailsData(taskId);
        setEmojiLoading(false);
      }
    },
    [closeEmojiDropdown, currentEmoji, taskId, workspace?.id]
  );

  const onReplyClick = useCallback(
    (item: CommentModel) => {
      setReplyMessage({
        id: item?.id,
        Message: item?.comment || '',
        Files: item?.Files,
        CreatedTime: item?.createdOn?.toISOString(),
        Sender: item?.createdBy?.name,
        Sender_id: item?.createdBy?.id
      });
      scrollToInline();
    },
    [scrollToInline, setReplyMessage]
  );

  const handleMenuClick = useCallback(
    (key: number | string, item: CommentModel) => {
      key = Number(key);
      if (key === MENU_OPTIONS.EDIT) {
        onEditComment(item);
      } else if (key === MENU_OPTIONS.DELETE) {
        onDeleteComment(item.id);
      } else if (key === MENU_OPTIONS.ADD_REACTION) {
        setSelectedMessage(item?.id);
        setIsEmojiModalOpen(true);
      } else if (key === MENU_OPTIONS.REPLY) {
        onReplyClick(item);
      }
    },
    [onEditComment, onDeleteComment, onReplyClick, setSelectedMessage]
  );

  const handleOpenChange = useCallback(
    (e: React.SyntheticEvent, itemId: string) => {
      e.stopPropagation();
      if (selectedMessage === itemId) {
        setOpenEmoji(false);
        setSelectedMessage(null);
      } else {
        setOpenEmoji(true);
        setSelectedMessage(itemId);
      }
    },
    [selectedMessage, setOpenEmoji, setSelectedMessage]
  );

  const closeEmojiModal = useCallback(() => {
    setIsEmojiModalOpen(false);
  }, []);

  // --- Render sub-components ---

  const RenderCommentHeader = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => {
      const commenter = item?.createdBy;
      const isClientUser = commenter?.role === USER_ROLE.CLIENT;
      const companyName = commenter?.customerCompanyName;
      const companyId = commenter?.customerCompanyId;

      return (
        <Div isComment>
          <Avatar
            imgSrc={commenter?.profile_photo || ''}
            name={commenter?.name || 'U N'}
            size={24}
            borderVariant={AVATAR_BORDER.NONE}
          />
          <ActivityContentView>
            <ActivitySubContent isComment>
              <ActivitySubContentLeft>
                <SubTitle>{commenter?.name}</SubTitle>
                {isClientUser && companyName && (
                  <CompanySection
                    role='button'
                    isViewerPrivileged={isViewerPrivileged}
                    onClick={() => handleCompanyClick(companyId, isViewerPrivileged)}>
                    <CompanyName>{companyName}</CompanyName>
                  </CompanySection>
                )}
              </ActivitySubContentLeft>
              <Tooltip
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}
                placement='top'
                maxWidth={'max-content'}>
                <Text isComment>{dueTime}</Text>
              </Tooltip>
            </ActivitySubContent>
          </ActivityContentView>
        </Div>
      );
    },
    [handleCompanyClick, isViewerPrivileged, userDetails?.dateFormat]
  );

  const RenderReply = useCallback(({ item }: { item: CommentModel }) => {
    if (!item?.reply?.id) return null;
    const classList = [
      'figma-main-div',
      'youtube-preview-main-div',
      'screen-recording-custom-main-div',
      'e-rte-image e-imginline',
      'project-link-embed-preview-main-div',
      'task-link-preview-main-div'
    ];
    const isShowInBlock = classList.some((className) => item?.reply?.comment?.includes(className));
    const hasTableInReply =
      !!item?.reply?.comment && (item.reply.comment.includes('tableWrapper') || /<table[\s>]/.test(item.reply.comment));
    const classForReply = isShowInBlock ? 'text-block' : 'text';
    const handleReplyClick = () => {
      const repliedComment = document.getElementById(item?.reply?.id || '');
      if (repliedComment) {
        repliedComment.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    const replyCommenter = item?.reply?.createdBy;
    const isClientUser = replyCommenter?.role === USER_ROLE.CLIENT;
    const companyName = replyCommenter?.customerCompanyName;
    return (
      <div className='thread-div' onClick={handleReplyClick}>
        <div className='content'>
          <div className='line' />
          <div className={`right-div${hasTableInReply ? ' right-div-overflow' : ''}`}>
            <div className='header'>
              <div className='left-header'>
                <ActivitySubContentLeft>
                  <div className='name'>{item?.reply?.createdBy?.name}</div>
                  {isClientUser && companyName && (
                    <CompanySection>
                      <CompanyName>{companyName}</CompanyName>
                    </CompanySection>
                  )}
                </ActivitySubContentLeft>
                <div className='time'>{dayjs(item?.reply?.createdOn).fromNow()}</div>
              </div>
            </div>
            {!isEmpty(item?.reply?.comment) && (
              <div className={classForReply}>
                <RenderHtml htmlString={item?.reply?.comment || ''} isReplyText={true} />
              </div>
            )}
            {item?.reply?.Files && renderFiles(item.reply.Files)}
          </div>
        </div>
      </div>
    );
  }, []);

  const RenderFileList = useCallback(
    ({ files }: { files: CommentFilesInterface[] }) => {
      if (!files.length) return null;
      const { imageFiles, videoFiles, audioFiles, otherFiles } = categorizeFiles(files);
      return (
        <FileSection>
          {imageFiles.map((item) => (
            <RenderImagePreview key={item?.href} imageUrl={item?.href} altText={item?.name} />
          ))}
          {videoFiles.map((item) => (
            <RenderVideoFile key={item?.href} videoUrl={item?.href} videoName={item?.name} />
          ))}
          {audioFiles.map((item) => (
            <audio controls key={item?.href}>
              <source type='audio/mp3' src={item?.href} />
            </audio>
          ))}
          {otherFiles.map((file) => (
            <RenderFilePreview
              key={file.href}
              file={file}
              maxWidth={240}
              onClickUpdate={(e) => handleActivityFileCard(e, file)}
            />
          ))}
        </FileSection>
      );
    },
    [handleActivityFileCard]
  );

  const RenderCommentBody = useCallback(
    ({ item }: { item: CommentModel }) => {
      const emptyDescPattern = /^<p>(?:\s|<br\s*\/?>)*<\/p>$/;
      return (
        <Div2>
          {!emptyDescPattern.test(item?.comment || '') && !isEmpty(item?.comment) && (
            <SubText
              role='button'
              figmaRefreshLoading={figmaRefreshLoading !== null}
              className='tiptap'
              onClick={(e) => onClickHandleRichtextClick(e, item)}>
              <RenderHtml
                htmlString={item?.comment || ''}
                figmaLoading={figmaRefreshLoading === item?.id}
                onClickRefreshFigma={(figmaURL) => onClickHandleRefreshFigma(item, figmaURL)}
              />
            </SubText>
          )}
          <RenderFileList files={item?.Files || []} />
        </Div2>
      );
    },
    [RenderFileList, figmaRefreshLoading, onClickHandleRefreshFigma, onClickHandleRichtextClick]
  );

  // --- Activity type renderers ---

  const RenderSetDueDate = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='calendar-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                set due date to <Text>{dayjs(item?.dueDate).format(userDetails?.dateFormat)}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}
                maxWidth={'max-content'}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderChangeDueDate = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='calendar-icon'
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                changed due date from <span>{dayjs(item?.previousDueDate).format(userDetails?.dateFormat)}</span> to{' '}
                <Text>{dayjs(item?.dueDate).format(userDetails?.dateFormat)}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}
                maxWidth={'max-content'}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderAddAssignee = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => {
      const assignee = workspace?.users?.find((u) => u?.id === item?.assigneeId);
      return (
        <>
          <Avatar
            imgSrc={item?.createdBy?.profile_photo || ''}
            name={item?.createdBy?.name || 'U N'}
            size={24}
            borderVariant={AVATAR_BORDER.NONE}
          />
          <ActivityContentView>
            <ActivitySubContent>
              <SubTitle>
                {item?.createdBy?.name || 'Unknown'}
                <Text>
                  assigned task to <Text>{assignee?.name}</Text>.
                </Text>
                <Tooltip
                  placement='bottom'
                  maxWidth={'max-content'}
                  title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                  <Text>{dueTime}</Text>
                </Tooltip>
              </SubTitle>
            </ActivitySubContent>
          </ActivityContentView>
        </>
      );
    },
    [userDetails?.dateFormat, workspace]
  );

  const RenderRemoveAssignee = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => {
      const assignee = workspace?.users?.find((u) => u?.id === item?.assigneeId);
      return (
        <>
          <Avatar
            imgSrc={item?.createdBy?.profile_photo || ''}
            name={item?.createdBy?.name || 'U N'}
            size={24}
            borderVariant={AVATAR_BORDER.NONE}
          />
          <ActivityContentView>
            <ActivitySubContent>
              <SubTitle>
                {item?.createdBy?.name || 'Unknown'}
                <Text>
                  unassigned task to <Text>{assignee?.name}</Text>.
                </Text>
                <Tooltip
                  placement='bottom'
                  maxWidth={'max-content'}
                  title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                  <Text>{dueTime}</Text>
                </Tooltip>
              </SubTitle>
            </ActivitySubContent>
          </ActivityContentView>
        </>
      );
    },
    [userDetails?.dateFormat, workspace?.users]
  );

  const RenderUnassign = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <Avatar
          imgSrc={item?.createdBy?.profile_photo || ''}
          name={item?.createdBy?.name || 'U N'}
          size={24}
          borderVariant={AVATAR_BORDER.NONE}
        />
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>removed assignee.</Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderChangePriority = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name={getPriorityIcon(item?.priority || TASK_PRIORITY.NO_PRIORITY)}
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                changed priority from{' '}
                <Text>
                  {item?.previousPriority !== undefined &&
                    item?.previousPriority !== null &&
                    TASK_PRIORITY_LABELS[Number(item?.previousPriority)]}
                </Text>{' '}
                to{' '}
                <Text>
                  {item?.priority !== undefined && item?.priority !== null && TASK_PRIORITY_LABELS[item?.priority]}
                </Text>
                .
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [getPriorityIcon, userDetails?.dateFormat]
  );

  const RenderChangeGroup = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <Avatar
          imgSrc={item?.createdBy?.profile_photo || ''}
          name={item?.createdBy?.name || 'U N'}
          size={24}
          borderVariant={AVATAR_BORDER.NONE}
        />
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                changed group from <Text>{item?.previousGroup}</Text> to <Text>{item?.group}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderUpdateTask = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='edit-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>updated task.</Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderRemoveDueDate = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='calendar-icon'
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>removed due date.</Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderStatusChange = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name={item?.status ? 'activity-completedtask-icon' : 'activity-reopentask-icon'}
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>{item?.status ? 'completed this task.' : 're‑opened this task.'}</Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderSetEstimate = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='time-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                set estimate time to{' '}
                <Text>
                  {item?.estimateTime !== 0 &&
                    item?.estimateTime !== null &&
                    item?.estimateTime !== undefined &&
                    floatToHHMM(item?.estimateTime)}{' '}
                  hours.
                </Text>
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderAudio = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <Avatar
          imgSrc={item?.createdBy?.profile_photo || ''}
          name={item?.createdBy?.name || 'U N'}
          size={24}
          borderVariant={AVATAR_BORDER.NONE}
        />
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
          <SubText>
            <audio controls>
              <source type='audio/mp3' src={item?.comment || ''} />
            </audio>
          </SubText>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderSetMilestone = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='milestone-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                added this task in milestone <Text>{item?.group}.</Text>
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderAddLabel = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='updated-label-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                added label <Text>{item?.group}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderRemoveLabel = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <EditIcon>
          <SVGIcon
            name='updated-label-icon'
            width={ICON_SIZE.SM}
            height={ICON_SIZE.SM}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </EditIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.createdBy?.name || 'Unknown'}
              <Text>
                removed label <Text>{item?.group}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [userDetails?.dateFormat]
  );

  const RenderChangeStatusId = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <StatusIcon>{renderActivityStatusIcon(item?.priority || 1)}</StatusIcon>
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {item?.isGithub ? 'Github' : item?.createdBy?.name || 'Unknown'}
              <Text>
                changed status from <Text>{item?.previousGroup}</Text> to <Text>{item?.group}</Text>.
              </Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [renderActivityStatusIcon, userDetails?.dateFormat]
  );

  const RenderTaskCreateActivity = useCallback(
    ({ item, dueTime }: { item: CommentModel; dueTime: string }) => (
      <>
        <Avatar
          imgSrc={item?.createdBy?.profile_photo ? item?.createdBy?.profile_photo : ''}
          name={taskDetails?.createdBy === 'sentry' ? 'sentry' : item?.createdBy?.name || 'U N'}
          size={24}
          borderVariant={AVATAR_BORDER.NONE}
        />
        <ActivityContentView>
          <ActivitySubContent>
            <SubTitle>
              {taskDetails?.createdBy === 'sentry' ? 'Sentry' : item.createdBy?.name || 'Unknown'}
              <Text>created the task.</Text>
              <Tooltip
                placement='bottom'
                maxWidth={'max-content'}
                title={dayjs(item?.createdOn).format(`${userDetails?.dateFormat}, h:mm:ss a`)}>
                <Text>{dueTime}</Text>
              </Tooltip>
            </SubTitle>
          </ActivitySubContent>
        </ActivityContentView>
      </>
    ),
    [taskDetails?.createdBy, userDetails?.dateFormat]
  );

  const renderActivity = useCallback(
    (item: CommentModel, dueTime: string) => {
      if (!item) return null;

      const commentTypeToComponent = {
        [COMMENT_TYPE.SET_DUE_DATE]: <RenderSetDueDate item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.CHANGE_DUE_DATE]: <RenderChangeDueDate item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.ADD_ASSIGNEE]: <RenderAddAssignee item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.REMOVE_ASSIGNEE]: <RenderRemoveAssignee item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.UNASSIGN]: <RenderUnassign item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.CHANGE_PRIORITY]: <RenderChangePriority item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.CHANGE_GROUP]: <RenderChangeGroup item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.UPDATE_TASK]: <RenderUpdateTask item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.REMOVE_DUE_DATE]: <RenderRemoveDueDate item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.STATUS_CHANGE]: <RenderStatusChange item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.ESTIMATE_TIME]: <RenderSetEstimate item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.AUDIO]: <RenderAudio item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.SET_MILESTONE]: <RenderSetMilestone item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.ADD_LABEL]: <RenderAddLabel item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.REMOVE_LABEL]: <RenderRemoveLabel item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.STATUS_ID_CHANGE]: <RenderChangeStatusId item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.CREATE_TASK]: <RenderTaskCreateActivity item={item} dueTime={dueTime} />,
        [COMMENT_TYPE.COMMENT]: (
          <div id={item?.id}>
            <RenderCommentHeader item={item} dueTime={dueTime} />
            <RenderReply item={item} />
            <RenderCommentBody item={item} />
            <CommentReactions
              item={item}
              emojiLoading={emojiLoading}
              workspaceUsers={workspace?.users}
              onReactOnMessage={onReactOnMessage}
            />
          </div>
        )
      };

      return commentTypeToComponent[item?.type || COMMENT_TYPE.COMMENT] || null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [RenderCommentBody, emojiLoading, onReactOnMessage, workspace?.users]
  );

  // --- Menu renderers ---

  const renderMoreMenuItems = useCallback(
    (item: CommentModel) => {
      return dropdownMenuItems.map((menuItem) => (
        <DropdownItem
          key={menuItem.key}
          label={menuItem.label}
          onClick={() => handleMenuClick(menuItem.key, item)}
          tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
        />
      ));
    },
    [handleMenuClick]
  );

  const renderResponsiveMoreMenuItems = useCallback(
    (item: CommentModel) => {
      let menuItemsToShow = responsiveDropdownMenuItems;
      if (item?.createdBy?.id !== currentUser?.id) {
        menuItemsToShow = responsiveDropdownMenuItems.filter(
          (menuItem) => menuItem.key === MENU_OPTIONS.ADD_REACTION || menuItem.key === MENU_OPTIONS.REPLY
        );
      }
      return menuItemsToShow.map((menuItem) => (
        <DropdownItem
          key={menuItem.key}
          label={menuItem.label}
          iconName={menuItem.iconName}
          iconSize={18}
          iconViewBox='0 0 18 18'
          onClick={() => handleMenuClick(menuItem.key, item)}
          tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
        />
      ));
    },
    [currentUser?.id, handleMenuClick]
  );

  // --- Existing memos ---

  const createdByData = useMemo(() => {
    return workspace?.users?.find((user) => user?.id === taskDetails?.createdBy);
  }, [taskDetails?.createdBy, workspace?.users]);

  // Not memoized: editing a comment keeps the same array and length, so a useMemo would
  // keep returning the old comment and the edited text would never show up.
  const sortedComments: CommentModel[] =
    taskDetails?.comments?.slice(0).sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime()) || [];

  return (
    <>
      <Activity>
        <ActivityTitle ref={inlineScrollRef}>Activity</ActivityTitle>
        <ActivityView isKeyboardVisible={isKeyboardVisible}>
          <ItemBlock>
            {sortedComments?.[0]?.type !== COMMENT_TYPE.CREATE_TASK && (
              <ItemChange>
                <Avatar
                  imgSrc={createdByData?.profile_photo ? createdByData?.profile_photo : ''}
                  name={taskDetails?.createdBy === 'sentry' ? 'sentry' : createdByData?.name || 'U N'}
                  size={24}
                  borderVariant={AVATAR_BORDER.NONE}
                />
                <ActivityContentView>
                  <ActivitySubContent>
                    <SubTitle>
                      {taskDetails?.createdBy === 'sentry' ? 'Sentry' : createdByData?.name || 'Unknown'}
                      <Text>created the task.</Text>
                      <Tooltip
                        placement='bottom'
                        maxWidth={'max-content'}
                        title={dayjs(taskDetails?.createdOn).format(
                          `${userDetails?.dateFormat || DEFAULT_DATE_FORMAT}, h:mm:ss a`
                        )}>
                        <Text>{dayjs(taskDetails?.createdOn).fromNow()}</Text>
                      </Tooltip>
                    </SubTitle>
                  </ActivitySubContent>
                </ActivityContentView>
              </ItemChange>
            )}
            {sortedComments?.map((item, index) => {
              const dueTime = dayjs(item?.createdOn).fromNow();
              if (item?.isArchived) return <React.Fragment key={item?.id}></React.Fragment>;
              if (!buttonDropdownRef.current[index]) {
                buttonDropdownRef.current[index] = React.createRef<HTMLDivElement>();
              }
              if (!emojiButtonRef.current[index]) {
                emojiButtonRef.current[index] = React.createRef<HTMLDivElement>();
              }
              return (
                <React.Fragment key={item?.id}>
                  <ItemChange
                    key={item?.id}
                    isHover={item?.type === COMMENT_TYPE.COMMENT}
                    isApplyBottomSpacing={
                      sortedComments?.length > index + 1 &&
                      item?.type === COMMENT_TYPE.COMMENT &&
                      sortedComments?.[index + 1]?.type !== COMMENT_TYPE.COMMENT
                    }
                    hasNextComment={
                      item?.type === COMMENT_TYPE.COMMENT && sortedComments?.[index - 1]?.type === COMMENT_TYPE.COMMENT
                    }
                    isActive={selectedMessage === item?.id}>
                    {item?.type !== COMMENT_TYPE.COMMENT &&
                      item?.type !== COMMENT_TYPE.CREATE_TASK &&
                      sortedComments?.[index - 1]?.type !== COMMENT_TYPE.COMMENT && <BorderDiv />}
                    {renderActivity(item, dueTime)}
                    {item?.type === COMMENT_TYPE.COMMENT && (
                      <RightSection className='right-section'>
                        <Portion>
                          {!mobile && (
                            <ReactionDiv
                              ref={emojiButtonRef.current[index]}
                              isActive={(openEmoji && selectedMessage === item?.id) || selectedMessage === item?.id}
                              onClick={(e: React.SyntheticEvent) => handleOpenChange(e, item?.id)}>
                              <IconButton
                                iconName='reaction-icon'
                                size={ICON_BUTTON_SIZE.MEDIUM}
                                variant={ICON_BUTTON_TYPE.TERTIARY}
                                className={openEmoji && selectedMessage === item?.id ? 'active' : ''}
                              />
                              {openEmoji && selectedMessage === item?.id && (
                                <Emoji
                                  setIsOpenEmoji={setOpenEmoji}
                                  isOpenEmoji={openEmoji}
                                  setOpenDropdownId={setSelectedMessage}
                                  emojiButtonRef={emojiButtonRef.current[index]}
                                  onEmojiClick={(emojiData: EmojiClickData) => onReactOnMessage(emojiData, item)}
                                  isEmojiLoading={emojiLoading}
                                  isEmojiModal={mobile ? true : false}
                                />
                              )}
                            </ReactionDiv>
                          )}
                          {!mobile && (
                            <IconButton
                              iconName='reply-icon'
                              size={ICON_BUTTON_SIZE.MEDIUM}
                              variant={ICON_BUTTON_TYPE.TERTIARY}
                              onClick={() => onReplyClick(item)}
                            />
                          )}
                          {(item?.createdBy?.id === currentUser?.id || mobile) && (
                            <Dropdown
                              activeClassName='active'
                              modalTitle='Manage Comment'
                              content={
                                <IconButton
                                  iconName='three-dots-horizontal-icon'
                                  size={ICON_BUTTON_SIZE.MEDIUM}
                                  variant={ICON_BUTTON_TYPE.TERTIARY}
                                  onClick={() => setSelectedMessage(item?.id)}
                                />
                              }
                              trigger='click'
                              onOutsideClick={() => setSelectedMessage(null)}>
                              {mobile ? renderResponsiveMoreMenuItems(item) : renderMoreMenuItems(item)}
                            </Dropdown>
                          )}
                        </Portion>
                      </RightSection>
                    )}
                  </ItemChange>
                </React.Fragment>
              );
            })}
          </ItemBlock>
          <CommentInputBox
            taskId={taskId || ''}
            projectId={taskDetails?.projectId || currentProject?.id || ''}
            onSend={onSendComment}
            disable={isOpenComment}
            users={
              (currentProject?.users as UserInterface[])?.filter((user: UserInterface) => !isEmpty(user?.id)) || []
            }
            onClickSelectFile={() => attachmentPage(false)}
            openModal={openModal}
            handleOpenSnapModal={handleOpenSnapModal}
            ref={inlineRef}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={onFileChange}
            replyMessage={replyMessage}
            setReplyMessage={() => setReplyMessage({})}
            propsLoading={loading}
            setPropsLoading={(value: boolean) => setLoading(value)}
          />
        </ActivityView>
      </Activity>
      <Modal open={isEmojiModalOpen} onClose={closeEmojiModal}>
        <Emoji
          setIsOpenEmoji={setIsEmojiModalOpen}
          isOpenEmoji={isEmojiModalOpen}
          setOpenDropdownId={setSelectedMessage}
          emojiButtonRef={emojiButtonRef?.current[0]}
          onEmojiClick={(emojiData: EmojiClickData) => {
            if (selectedMessage) onReactOnMessage(emojiData, { id: selectedMessage });
          }}
          isEmojiLoading={emojiLoading}
          isEmojiModal={true}
        />
      </Modal>
    </>
  );
});

export default TaskActivity;
